/** @jsx jsx */
import { jsx } from '@emotion/core'
import React, { useState } from 'react'
import _ from 'lodash'
import { MatchRecordWrap } from '@views/MatchRecord/index.style'
import { MatchTeamCommonWrap } from '@styles/match.style'
import { MatchTimeline } from '@views/MatchRecord/MatchTimeline'
import { MatchTimeTable } from '@views/MatchRecord/MatchTimeTable'
import { SimpleMatchTimelineGroup } from '@interface/chat'
import { useOnMount, useOnUnmount } from '@utils/reactExt'
import useRootStore from '@store/useRootStore'
import classNames from 'classnames'
import { observer } from 'mobx-react'
import { MATCH_GAME_STATUS, MATCH_STATUS, SPORT_ID } from 'adv-protocol/src/GameCommon'
import { BASIC_TEAM_EMBLEM_MAP } from '@shared/EmblemGroup'
import { GlobalStoreKey } from '@v2/constants/common'

export interface MatchRecordSimpleMatchInfo {
  matchId: number
  sportId: number
  homeScore: number
  awayScore: number
  homeTeamName: string
  awayTeamName: string
  homeTeamEmblem: string
  awayTeamEmblem: string
  matchStatusName?: string
  isLiveMatch: boolean
}

interface Props {
  matchRecordSimpleMatchInfo: MatchRecordSimpleMatchInfo
}

let getSimpleMatchTimelineGroupTimer: NodeJS.Timer

export const MatchRecord: React.FC<Props> = observer((props: Props) => {
  const { matchupStore, globalStore } = useRootStore()
  const { matchRecordSimpleMatchInfo } = props
  const [
    simpleMatchTimelineGroup,
    setSimpleMatchTimelineGroup,
  ] = useState<SimpleMatchTimelineGroup>()
  const {
    matchId, matchStatusName, sportId,
    homeTeamName, homeTeamEmblem,
    awayTeamName, awayTeamEmblem,
  } = matchRecordSimpleMatchInfo

  useOnMount(() => {
    fetchSimpleMatchTimelineGroup()
  })

  const fetchSimpleMatchTimelineGroup = () => {
    matchupStore.getSimpleMatchTimelineGroup(matchId, true).then((timelineGroup) => {
      const interval = _.toNumber(
        globalStore.findGlobalSetting(GlobalStoreKey.MATCH_TIME_LINE_REFRESH_INTERVAL_TIME)?.value ?? 20,
      ) * 1000
      setSimpleMatchTimelineGroup(timelineGroup)
      const simpleMatchTimeline = timelineGroup?.simpleMatchTimelineList?.[0]
      if (_.isEmpty(simpleMatchTimeline)) {
        return
      }

      if (!(simpleMatchTimeline.matchStatusCode === MATCH_STATUS.Ended
        || simpleMatchTimeline.statusCode === MATCH_GAME_STATUS.Closed)) {
        getSimpleMatchTimelineGroupTimer = setTimeout(() => {
          fetchSimpleMatchTimelineGroup()
        }, interval)
      }
    }).catch(console.error)
  }

  useOnUnmount(() => {
    clearTimeout(getSimpleMatchTimelineGroupTimer)
  })
  const simpleMatchTimeline = simpleMatchTimelineGroup?.simpleMatchTimelineList?.[0]
  const homeScore = simpleMatchTimeline?.homeScore ?? 0
  const awayScore = simpleMatchTimeline?.awayScore ?? 0
  const homeScoreClassName = classNames('score', {
    win: homeScore > awayScore,
  })
  const awayScoreClassName = classNames('score', {
    win: awayScore > homeScore,
  })

  let isOverTimeMatch = false
  let isPenaltiesMatch = false

  const lastPeriod = _.last(simpleMatchTimeline?.periodScoreList)
  if (SPORT_ID.SOCCER === sportId || SPORT_ID.ICE_HOCKEY === sportId || SPORT_ID.HANDBALL === sportId) {
    if (!_.isEmpty(lastPeriod)) {
      if (lastPeriod.matchStatusCode === MATCH_STATUS.Overtime) {
        isOverTimeMatch = true
      } else if (lastPeriod.matchStatusCode === MATCH_STATUS.Penalties) {
        isPenaltiesMatch = true
      }
    }
  }

  let updatedMatchStatusName = simpleMatchTimeline?.matchStatusName ?? matchStatusName
  if (isOverTimeMatch) {
    updatedMatchStatusName = '연장'
  } else if (isPenaltiesMatch) {
    updatedMatchStatusName = '승부'
  }

  return (
    <MatchRecordWrap>
      <MatchTeamCommonWrap>
        <span className="team-info-item">
          <img src={homeTeamEmblem || BASIC_TEAM_EMBLEM_MAP[sportId]} alt="홈팀 엠블럼" className="emblem"/>
          <span className="team">{homeTeamName}</span>
          <span className="home-tag-img"/>
        </span>
        <span className="team-info-item center">
            <span className={homeScoreClassName}>{homeScore || 0}</span>
            <div className="vs-bg"></div>
            <span className={awayScoreClassName}>{awayScore || 0}</span>
            <div className="match-tag">{updatedMatchStatusName}</div>
        </span>
        <span className="team-info-item">
          <img src={awayTeamEmblem || BASIC_TEAM_EMBLEM_MAP[sportId]} alt="원정팀 엠블럼" className="emblem"/>
          <span className="team">{awayTeamName}</span>
        </span>
      </MatchTeamCommonWrap>

      <MatchTimeTable
        matchRecordSimpleMatchInfo={matchRecordSimpleMatchInfo}
        simpleMatchTimeline={simpleMatchTimeline}
      />
      <MatchTimeline
        matchRecordSimpleMatchInfo={matchRecordSimpleMatchInfo}
        simpleMatchTimeline={simpleMatchTimeline}
      />
    </MatchRecordWrap>
  )
})
